import { h, FunctionalComponent } from 'preact';
import { useContext, useEffect, useRef } from 'preact/hooks';
import { layoutStore } from 'global/layout.store';
import { useTranslation } from 'services/use-translation';
import { OversizedIcon } from '@uw/icons';
import { ToggleButton } from '@uw/common-components';
import { isMobile } from 'helpers/detect-user-agent';
import { OVERSIZED_CLASS, oversizedStore } from './oversized.store';
import { useConnect } from '../../helpers/use-connect-to-global-store';
import { useDynamicSize } from './use-dynamic-size';
import { ApplicationContext } from '../../services/use-application-setup';
import './oversized-widget.scss';
import { useNavigationReader } from '../../services/use-navigation-reader';

export const OversizedWidget: FunctionalComponent = () => {
  const { translate } = useTranslation();
  const { sendNavigationReaderRequest } = useNavigationReader();
  const { config } = useContext(ApplicationContext);

  const services = config.current.services;
  const isPaidAI = services ? services.paidAi : null;
  const isLST = services ? services.LIVE_TRANSLATIONS : null;

  const size = useDynamicSize();

  const [isOversized, setIsOversized] = useConnect(oversizedStore.isOversized$);

  const toggleButton = useRef<HTMLInputElement>(null);

  useEffect(() => {
    isOversized
      ? layoutStore.mainContainer.addClass(OVERSIZED_CLASS)
      : layoutStore.mainContainer.removeClass(OVERSIZED_CLASS);
  }, [isOversized]);

  const toggle = (value: boolean) => {
    const nextValue = !value;

    setIsOversized(nextValue);

    const contextToRead = translate(nextValue
      ? 'widget.oversized_widget.toggle_button.enabled'
      : 'widget.oversized_widget.toggle_button.disabled',
    );

    sendNavigationReaderRequest(contextToRead);
  };

  const isOversizedViewDisabled = isMobile() ||
    (config.current.services?.MODIFY_MENU
      && !config.current.services.MODIFY_MENU.oversized);

  if (isOversizedViewDisabled) {
      setIsOversized(false);
      return null;
  }

  const isOversizedIcon = () => isPaidAI || isLST;

  return (
    <div className="uwaw-oversized">
      <div className="uwaw-option">
        <div className="uwaw-option__i">
          <div className="uwaw-oversized__wrapper uwaw-switcher-wrap uwaw-switcher-wrap_fw">
            {
              isOversizedIcon() ?
                  /* @ts-expect-error preact SVG typing does not fully support all attributes */
                <OversizedIcon svgAttrs={{ 'aria-hidden': 'true' }}
                               className="uwaw-oversized__icon"
                               width={size(24, 32)}
                               height={size(24, 32)}
                /> : null
            }

            <ToggleButton
              reference={toggleButton}
              label={translate('widget.oversized_widget.toggle_button.label')}
              data-uw-reader-content={translate('widget.oversized_widget.toggle_button.ariaLabel')}
              ariaLabel={translate('widget.oversized_widget.toggle_button.ariaLabel')}
              checked={isOversized}
              onChange={() => toggle(isOversized)}
              labelOn={translate('widget.oversized_widget.toggle_button.on')}
              labelOff={translate('widget.oversized_widget.toggle_button.off')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
